import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiResponse } from '../types/bewerber';
import type {
  AusschreibungBoardMeta,
  AusschreibungCard,
  AusschreibungEvent,
  AusschreibungKanal,
  AusschreibungKanalState,
  StellenanzeigeStatus,
  WebsiteAktion,
  WebsitePublishMeta,
} from '../types/stellenausschreibung';
import { AusschreibungScope } from '../types/stellenausschreibung';
import { bedarfActivityKeys } from './activities';
import { apiClient } from './client';

// Query keys
export const stellenausschreibungenKeys = {
  all: ['stellenausschreibungen'] as const,
  boards: () => [...stellenausschreibungenKeys.all, 'board'] as const,
  board: (scope: AusschreibungScope) =>
    [...stellenausschreibungenKeys.boards(), scope] as const,
  events: (days: number) =>
    [...stellenausschreibungenKeys.all, 'events', days] as const,
};

const basePath = '/stellenausschreibungen';

/** Der Scope steht im Pfad, weil er bestimmt, was `refId` bedeutet. */
const scopeSegment = (scope: AusschreibungScope) =>
  scope === AusschreibungScope.ORT ? 'ort' : 'bedarf';

// API functions
const fetchBoard = async (
  scope: AusschreibungScope,
): Promise<ApiResponse<AusschreibungCard[]> & { meta: AusschreibungBoardMeta }> => {
  const response = await apiClient.get(basePath, { params: { scope } });
  return response.data;
};

const fetchEvents = async (
  days: number,
): Promise<ApiResponse<AusschreibungEvent[]>> => {
  const response = await apiClient.get(`${basePath}/events`, { params: { days } });
  return response.data;
};

export interface KanalMutationInput {
  scope: AusschreibungScope;
  refId: string;
  kanal: AusschreibungKanal;
  status: StellenanzeigeStatus;
  url?: string;
  /** Nur fürs Invalidieren der Timeline — der Server braucht ihn nicht. */
  bedarfId?: string;
}

/** `meta.website` sagt, ob die Anzeige auf der eigenen Website mitgezogen wurde. */
type KanalAntwort = ApiResponse<AusschreibungKanalState[]> & {
  meta?: { website: WebsiteAktion | null };
};

const upsertKanal = async ({
  scope,
  refId,
  kanal,
  status,
  url,
}: KanalMutationInput): Promise<KanalAntwort> => {
  const response = await apiClient.put(
    `${basePath}/${scopeSegment(scope)}/${refId}/kanal/${kanal}`,
    { status, url },
  );
  return response.data;
};

export interface PublishWebsiteInput {
  scope: AusschreibungScope;
  refId: string;
}

/**
 * Legt die Anzeige auf www.juniva.de an und hakt den Kanal ab — ein Aufruf, kein
 * Abhaken hinterher. Die Jobs-API ist über die Id idempotent, ein zweiter Klick
 * erzeugt also keine zweite Anzeige.
 */
const publishWebsite = async ({
  scope,
  refId,
}: PublishWebsiteInput): Promise<
  ApiResponse<AusschreibungKanalState[]> & { meta: WebsitePublishMeta }
> => {
  const response = await apiClient.post(
    `${basePath}/${scopeSegment(scope)}/${refId}/website/publish`,
  );
  return response.data;
};

const removeKanal = async ({
  scope,
  refId,
  kanal,
}: Omit<KanalMutationInput, 'status' | 'url'>): Promise<KanalAntwort> => {
  const response = await apiClient.delete(
    `${basePath}/${scopeSegment(scope)}/${refId}/kanal/${kanal}`,
  );
  return response.data;
};

/**
 * Wie oft die Seite von sich aus nachsieht.
 *
 * Änderungen aus diesem Browser landen sofort im Board — die Bedarf-Mutationen
 * invalidieren den Key mit. Das Pollen ist für alles andere da: was Kolleginnen
 * anderswo verschieben, oder was der n8n-Workflow anlegt. Es läuft nur, solange
 * der Tab sichtbar ist (`refetchIntervalInBackground` bleibt aus), ein
 * weggeklickter Tab kostet also nichts.
 */
const POLL_INTERVALL_MS = 30_000;

/**
 * Kein `staleTime`: das Board ist eine Arbeitsfläche, auf der ein veralteter
 * Stand echte Arbeit auslöst — eine Anzeige, die längst abgeschaltet gehört. Die
 * Abfragen sind billig genug, dass Frischhalten günstiger ist als Raten.
 */
const LIVE_OPTIONS = {
  staleTime: 0,
  refetchInterval: POLL_INTERVALL_MS,
  // Der globale Default steht auf false; hier ist die Rückkehr in den Tab aber
  // genau der Moment, in dem man den aktuellen Stand sehen will.
  refetchOnWindowFocus: true,
} as const;

// Hooks
export const useAusschreibungBoard = (scope: AusschreibungScope) =>
  useQuery({
    queryKey: stellenausschreibungenKeys.board(scope),
    queryFn: () => fetchBoard(scope),
    ...LIVE_OPTIONS,
  });

export const useAusschreibungEvents = (days = 14) =>
  useQuery({
    queryKey: stellenausschreibungenKeys.events(days),
    queryFn: () => fetchEvents(days),
    ...LIVE_OPTIONS,
  });

/**
 * Beide Mutationen invalidieren neben dem Board auch den Ticker: dessen Zeilen
 * tragen den aktuellen Ausschreibungsstand mit, sonst behauptet er nach dem Haken
 * weiter "noch nicht ausgeschrieben".
 */
function useKanalInvalidation() {
  const queryClient = useQueryClient();
  return (input: { scope: AusschreibungScope; refId: string }) => {
    queryClient.invalidateQueries({ queryKey: stellenausschreibungenKeys.boards() });
    queryClient.invalidateQueries({
      queryKey: [...stellenausschreibungenKeys.all, 'events'],
    });
    if (input.scope === AusschreibungScope.BEDARF) {
      // Der Meilenstein-Eintrag hängt an der Bedarf-Timeline.
      queryClient.invalidateQueries({
        queryKey: bedarfActivityKeys.timelines(input.refId),
      });
    }
  };
}

export const useUpsertKanal = () => {
  const invalidate = useKanalInvalidation();
  return useMutation({
    mutationFn: upsertKanal,
    onSuccess: (_data, variables) => invalidate(variables),
  });
};

export const useRemoveKanal = () => {
  const invalidate = useKanalInvalidation();
  return useMutation({
    mutationFn: removeKanal,
    onSuccess: (_data, variables) => invalidate(variables),
  });
};

export const usePublishWebsite = () => {
  const invalidate = useKanalInvalidation();
  return useMutation({
    mutationFn: publishWebsite,
    onSuccess: (_data, variables) => invalidate(variables),
  });
};
